// src/pages/VulnerabilityComparison.jsx - Scan Comparison and Trending
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Grid, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { vulnerabilityApi } from '../services/api';
import { toast } from 'react-toastify';

const VulnerabilityComparison = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [currentScan, setCurrentScan] = useState(null);
  const [selectedScan, setSelectedScan] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    loadCurrentScan();
  }, [scanId]);

  useEffect(() => {
    if (currentScan) {
      loadClientScans();
    }
  }, [currentScan]);

  const loadCurrentScan = async () => {
    try {
      const response = await vulnerabilityApi.getScan(scanId);
      setCurrentScan(response.data);
    } catch (error) {
      toast.error('Failed to load scan');
      navigate('/vulnerability-scans');
    }
  };

  const loadClientScans = async () => {
    try {
      setLoading(true);
      const response = await vulnerabilityApi.getScans({ client_id: currentScan.client_id });
      // Filter out current scan and only show analyzed scans
      const otherScans = response.data.filter(
        s => s.id !== parseInt(scanId) && (s.status === 'analyzed' || s.status === 'report_generated')
      );
      setScans(otherScans);
    } catch (error) {
      toast.error('Failed to load scans');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!selectedScan) {
      toast.error('Please select a scan to compare');
      return;
    }

    try {
      setComparing(true);
      const response = await vulnerabilityApi.compareScans(scanId, selectedScan);
      setComparison(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!comparison) return;

    try {
      toast.info('Generating trending report...');
      await vulnerabilityApi.downloadComparisonReport(scanId, selectedScan);
      toast.success('Trending report downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate report');
    }
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUpIcon sx={{ color: '#e74c3c' }} />;
    if (change < 0) return <TrendingDownIcon sx={{ color: '#27ae60' }} />;
    return <TrendingFlatIcon sx={{ color: '#95a5a6' }} />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Critical: '#e74c3c',
      High: '#e67e22',
      Medium: '#f39c12',
      Low: '#3498db',
      Info: '#95a5a6'
    };
    return colors[severity] || '#95a5a6';
  };

  if (loading || !currentScan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/vulnerability-scans/${scanId}`)}
          sx={{ mb: 2, color: '#6B3FA0' }}
        >
          Back to Scan Details
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
          Scan Comparison & Trending
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare {currentScan.scan_name} with previous scans
        </Typography>
      </Box>

      {/* Scan Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="text.secondary">Current Scan</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currentScan.scan_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(currentScan.scan_date).toLocaleDateString()} • Risk Score: {currentScan.overall_score}/100
              </Typography>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="text.secondary">vs</Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel>Select Scan to Compare</InputLabel>
                <Select
                  value={selectedScan}
                  onChange={(e) => setSelectedScan(e.target.value)}
                  label="Select Scan to Compare"
                >
                  {scans.map((scan) => (
                    <MenuItem key={scan.id} value={scan.id}>
                      {scan.scan_name} - {new Date(scan.scan_date).toLocaleDateString()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCompare}
                disabled={!selectedScan || comparing}
                sx={{ mt: 2, bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
              >
                {comparing ? <CircularProgress size={24} color="inherit" /> : 'Compare Scans'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {scans.length === 0 && (
        <Alert severity="info">
          No other analyzed scans available for {currentScan.client_name}. Upload and analyze another scan to enable comparison.
        </Alert>
      )}

      {/* Comparison Results */}
      {comparison && (
        <>
          {/* Download Report Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
            >
              Download Trending Report
            </Button>
          </Box>

          {/* Score Trend */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Risk Score Trend</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Previous Scan</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', my: 1 }}>
                      {comparison.older_scan.score}
                    </Typography>
                    <Typography variant="caption">{comparison.older_scan.name}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Change</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 1 }}>
                      {getTrendIcon(comparison.score_trend.change)}
                      <Typography variant="h3" sx={{ fontWeight: 700, ml: 1, color: comparison.score_trend.change > 0 ? '#e74c3c' : comparison.score_trend.change < 0 ? '#27ae60' : '#95a5a6' }}>
                        {comparison.score_trend.change > 0 ? '+' : ''}{comparison.score_trend.change.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="caption">
                      {comparison.score_trend.percent_change > 0 ? '+' : ''}{comparison.score_trend.percent_change}%
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Current Scan</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', my: 1 }}>
                      {comparison.newer_scan.score}
                    </Typography>
                    <Typography variant="caption">{comparison.newer_scan.name}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Severity Trends */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Vulnerability Category Trends</Typography>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#ecf0f1' }}>
                    <TableRow>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell align="center"><strong>Previous</strong></TableCell>
                      <TableCell align="center"><strong>Change</strong></TableCell>
                      <TableCell align="center"><strong>Current</strong></TableCell>
                      <TableCell align="center"><strong>Trend</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(comparison.severity_trends).map(([severity, trend]) => (
                      <TableRow key={severity}>
                        <TableCell>
                          <Chip
                            label={severity.charAt(0).toUpperCase() + severity.slice(1)}
                            size="small"
                            sx={{ bgcolor: getSeverityColor(severity.charAt(0).toUpperCase() + severity.slice(1)), color: 'white', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">{trend.older_count}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {getTrendIcon(trend.change)}
                            <Typography sx={{ ml: 1, fontWeight: 600, color: trend.change > 0 ? '#e74c3c' : trend.change < 0 ? '#27ae60' : '#95a5a6' }}>
                              {trend.change > 0 ? '+' : ''}{trend.change}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{trend.newer_count}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${trend.percent_change > 0 ? '+' : ''}${trend.percent_change}%`}
                            size="small"
                            sx={{
                              bgcolor: trend.percent_change > 0 ? '#fadbd8' :
                                       trend.percent_change < 0 ? '#d4efdf' : '#ecf0f1',
                              color: trend.percent_change > 0 ? '#e74c3c' :
                                     trend.percent_change < 0 ? '#27ae60' : '#95a5a6'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#d4efdf', height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#27ae60', mb: 1 }}>
                    {comparison.remediated_count}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#229954' }}>
                    Vulnerabilities Remediated
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Fixed since previous scan
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#fadbd8', height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#e74c3c', mb: 1 }}>
                    {comparison.new_vulnerabilities_count}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#c0392b' }}>
                    New Vulnerabilities
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Discovered since previous scan
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Remediated Vulnerabilities */}
          {comparison.remediated_vulnerabilities.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#27ae60' }}>
                  Remediated Vulnerabilities ({comparison.remediated_count})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#d4efdf' }}>
                      <TableRow>
                        <TableCell><strong>Severity</strong></TableCell>
                        <TableCell><strong>Vulnerability</strong></TableCell>
                        <TableCell><strong>Device</strong></TableCell>
                        <TableCell><strong>CVSS</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.remediated_vulnerabilities.map((vuln, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Chip
                              label={vuln.severity}
                              size="small"
                              sx={{ bgcolor: getSeverityColor(vuln.severity), color: 'white' }}
                            />
                          </TableCell>
                          <TableCell>{vuln.nvt_name}</TableCell>
                          <TableCell>{vuln.device}</TableCell>
                          <TableCell>{vuln.cvss_score || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* New Vulnerabilities */}
          {comparison.new_vulnerabilities.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#e74c3c' }}>
                  New Vulnerabilities ({comparison.new_vulnerabilities_count})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#fadbd8' }}>
                      <TableRow>
                        <TableCell><strong>Severity</strong></TableCell>
                        <TableCell><strong>Vulnerability</strong></TableCell>
                        <TableCell><strong>Device</strong></TableCell>
                        <TableCell><strong>CVSS</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.new_vulnerabilities.map((vuln, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Chip
                              label={vuln.severity}
                              size="small"
                              sx={{ bgcolor: getSeverityColor(vuln.severity), color: 'white' }}
                            />
                          </TableCell>
                          <TableCell>{vuln.nvt_name}</TableCell>
                          <TableCell>{vuln.device}</TableCell>
                          <TableCell>{vuln.cvss_score || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default VulnerabilityComparison;
